import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Text } from '@chakra-ui/react';
import { PageHeader as ThemePageHeader } from '@themes/aegix';
import { useUser, type AllauthUser } from '@aegix/auth-identity';
import { ProfileDrawer } from '@/components/ProfileDrawer';

type ThemePageHeaderProps = React.ComponentProps<typeof ThemePageHeader>;

const SCREEN_READER_KEY = 'aegix_one_screen_reader';

function readInitialScreenReader(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SCREEN_READER_KEY) === 'true';
  } catch {
    return false;
  }
}

export function OnePageHeader(props: ThemePageHeaderProps) {
  const { t } = useTranslation();
  const user = useUser<AllauthUser>();
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [screenReaderEnabled, setScreenReaderEnabled] = useState<boolean>(
    readInitialScreenReader,
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SCREEN_READER_KEY,
        screenReaderEnabled ? 'true' : 'false',
      );
    } catch {
      // localStorage unavailable — ignore
    }
  }, [screenReaderEnabled]);

  const currentUser = user
    ? {
        name: `${user.first_name} ${user.last_name}`.trim() || user.email,
        email: user.email,
      }
    : { name: t('layout.user', 'User') };

  return (
    <>
      <ThemePageHeader
        currentUser={currentUser}
        // One click from the title bar to the tabbed Profile drawer.
        // Supplying the handler (and no `userMenuItems`) skips the theme's
        // intermediate "Account" drawer — Sign Out lives in the Profile
        // drawer's footer, so nothing is lost by dropping the menu.
        onUserMenuClick={() => setProfileDrawerOpen(true)}
        showOptionsDrawer
        screenReaderEnabled={screenReaderEnabled}
        onScreenReaderToggle={setScreenReaderEnabled}
        optionsDrawerTitle={t('layout.options')}
        themeLabel={t('layout.theme')}
        screenReaderTitle={t('layout.screenReader')}
        notificationsDrawerTitle={t('layout.notifications')}
        notificationsDrawerContent={
          <Box>
            <Text color="text.primary" fontSize="16px" fontWeight={500}>
              {t('layout.noNewNotifications')}
            </Text>
            <Text color="text.secondary" fontSize="14px" mt={2}>
              {t('layout.willSeeAlerts')}
            </Text>
          </Box>
        }
        {...props}
      />
      <ProfileDrawer
        isOpen={profileDrawerOpen}
        onClose={() => setProfileDrawerOpen(false)}
      />
    </>
  );
}
